import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, ChevronLeft, ChevronRight, CircleDot, Clock3, CreditCard, Eye, EyeOff, Flag as FlagIcon, Globe2, Laptop,
  Megaphone, MonitorSmartphone, PlayCircle, ShoppingBag, SlidersHorizontal, Sparkles, TriangleAlert, X,
} from 'lucide-react';

import { loadStorefrontFilters, loadStorefrontSession, loadStorefrontSessions } from './api';
import { Empty, Failed, Loading } from './parts';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import { FilterMenu, Flag, NUMBER, Panel, lengthOf } from './MobileParts';
import {
  ChannelMark, DeviceMark, EventIcon, PathText, ReadState, StorefrontGate, StorefrontHead, channelLabel, clickIdName, clickedName, clock, codeText, count,
  currencyName, dateTime, deviceLabel, duration, eventLabel, eventTone, inWords, languageName, money, st, stCount, useStoreNames,
  useStorefrontSite,
} from './StorefrontParts';
import StorefrontReplay from './StorefrontReplay';
import { OrderReference } from './StorefrontCampaigns';

// Every visit to the store, one at a time: where it came from, each page it
// saw, what it clicked, what went in the bag and whether it paid, with the
// recording when there is one. Filters live in the address, so any page can
// link to "the sessions from this campaign" or "the sessions that saw this page".

const PAGE_SIZE = 50;
const FLAGS = ['converted', 'bag', 'checkout', 'replay', 'errors'];
const TEXT_FILTERS = ['channel', 'device', 'country', 'campaign', 'link', 'path', 'landing', 'source', 'browser'];
// Events that say what the page did rather than what the visitor did stay folded away.
const QUIET = new Set(['visibility', 'element_visible', 'product_impression', 'web_vital', 'page_leave']);
const MARKED = { click: 'click', rage_click: 'bad', dead_click: 'bad', bag_add: 'commerce', checkout_step: 'commerce', payment_started: 'commerce', purchase: 'good', error: 'bad', checkout_error: 'bad', payment_failed: 'bad', back: 'commerce' };
// A page the browser's Back brought back arrives as the site's custom event
// nav.back; a session shows it as its own kind of event, "Pressed back".
const asShown = (event) => (event.type === 'custom' && event.props?.name === 'nav.back' ? { ...event, type: 'back' } : event);

function dayHeading(at, now) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (at >= start.getTime()) return st('today');
  if (at >= start.getTime() - 86400000) return st('yesterday');
  return new Date(at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

export function placeOf(session) {
  return session.city || (session.country ? countryName(session.country) : st('place_unknown'));
}

// What was clicked, as a visitor saw it (quoted when it had a name), and the
// section it sat in. Never the page's code.
function elementText(element) {
  if (!element) return '';
  const named = Boolean(element.text || element.ariaLabel || element.track);
  const where = element.section ? st('event.in_section', { section: element.section }) : '';
  return [named ? `“${clickedName(element)}”` : clickedName(element), where].filter(Boolean).join(' ');
}

// An amount the tracker sent, in its currency's minor unit; nothing when it sent none.
function eventMoney(amount, currency) {
  return Number.isInteger(amount) && currency ? money(amount, currency) : null;
}

// One event's detail line, from the props its type carries. `names` (from
// useStoreNames) names products; without it a product shows by its code.
export function eventDetail(event, names = null) {
  const props = event.props || {};
  const product = (code) => (names ? names.product(code) : code);
  switch (event.type) {
    case 'page_view': return props.loadMs ? st('event.loaded_in', { time: duration(props.loadMs) }) : (props.title || '');
    case 'page_leave': return st('event.left_after', { time: duration(props.activeMs), depth: props.maxScrollPct === undefined ? 0 : props.maxScrollPct });
    case 'scroll': return st('event.scrolled_to', { depth: props.depth });
    case 'click': case 'dead_click': return elementText(props.element);
    case 'rage_click': return `${elementText(props.element)} ${st('event.times', { count: props.count || 0 })}`;
    case 'form': return st('event.form_line', { field: inWords(props.field || props.form || ''), action: props.action ? st(`event.form_${props.action}`) : '' });
    case 'element_visible': return st('event.seen_for', { key: inWords(props.key || ''), time: duration(props.ms) });
    case 'product_impression': return [product(props.productCode), props.list, props.position !== undefined ? `#${props.position}` : ''].filter(Boolean).join(' · ');
    case 'product_view': return [product(props.productCode), props.variant, eventMoney(props.price, props.currency)].filter(Boolean).join(' · ');
    case 'variant_select': return [product(props.productCode), props.variant].filter(Boolean).join(' · ');
    case 'bag_add': case 'bag_remove': return [product(props.productCode), props.variant, props.quantity ? `× ${props.quantity}` : '', eventMoney(props.price, props.currency)].filter(Boolean).join(' · ');
    case 'bag_view': return [stCount('items_count', Array.isArray(props.items) ? props.items.length : 0), eventMoney(props.value, props.currency)].filter(Boolean).join(' · ');
    case 'coupon_attempt': return [props.code, props.result ? st(`event.coupon_${props.result}`) : '', props.reason ? codeText('coupon_reason', props.reason) : ''].filter(Boolean).join(' · ');
    case 'checkout_step': return props.ms ? st('event.step_after', { step: codeText('checkout_step', props.step), time: duration(props.ms) }) : codeText('checkout_step', props.step);
    case 'checkout_error': return [props.step ? codeText('checkout_step', props.step) : '', props.code ? codeText('checkout_error', props.code) : ''].filter(Boolean).join(' · ');
    // A reference is null for staff who cannot read orders.
    case 'payment_started': case 'purchase': return [props.orderReference || st('reference_hidden'), eventMoney(props.value, props.currency)].filter(Boolean).join(' · ');
    case 'payment_failed': return [props.orderReference || st('reference_hidden'), props.code ? codeText('decline', props.code) : ''].filter(Boolean).join(' · ');
    case 'market_change': case 'currency_change': case 'language_change': return st('event.changed', { from: props.from || '', to: props.to || '' });
    case 'search': return props.results === undefined ? `“${props.query || ''}”` : st('event.search_line', { query: props.query || '', count: props.results });
    case 'tracking_view': return props.found ? st('event.order_found') : st('event.order_not_found');
    case 'error': return props.message || '';
    case 'web_vital': return `${props.name || ''} ${props.name === 'CLS' ? Number(props.value || 0).toFixed(3) : duration(props.value)}${props.rating ? ` · ${props.rating}` : ''}`;
    case 'visibility': return props.state === 'hidden' ? st('event.tab_hidden') : st('event.tab_visible');
    case 'copy': return stCount('characters_count', props.length || 0);
    case 'custom': return props.name || '';
    default: return '';
  }
}

function SessionRow({ session, active, onPick }) {
  return (
    <button type="button" className={`xo-mob-session-row xo-sf-session-row${active ? ' is-active' : ''}`} onClick={() => onPick(session.id)}>
      <span className="xo-mob-session-main">
        <strong>
          {session.country ? <Flag code={session.country} /> : <Globe2 aria-hidden="true" />}
          {placeOf(session)}
          {session.live && <i className="xo-mob-live-dot" title={st('sessions.live_now')} />}
        </strong>
        <small>
          <DeviceMark device={session.deviceType} />
          {clock(session.startedAt).slice(0, 5)}
          <i aria-hidden="true">·</i>{lengthOf(session.durationMs)}
          <i aria-hidden="true">·</i>{stCount('pages_count', session.pageViews)}
        </small>
      </span>
      <span className="xo-sf-session-badges">
        <ChannelMark channel={session.channel} withLabel={false} />
        {session.bag && !session.purchased && <ShoppingBag aria-label={st('sessions.had_bag')} className="is-bag" />}
        {session.checkout && !session.purchased && <CreditCard aria-label={st('sessions.reached_checkout')} className="is-checkout" />}
        {session.purchased && <Sparkles aria-label={st('sessions.bought')} className="is-bought" />}
        {session.errors > 0 && <TriangleAlert aria-label={st('sessions.had_errors')} className="is-bad" />}
        {session.hasReplay && <PlayCircle aria-label={st('sessions.has_replay')} className="is-replay" />}
      </span>
    </button>
  );
}

function SessionList({ data, selected, onPick, offset, onPage, now }) {
  const groups = useMemo(() => {
    const result = [];
    for (const session of data.sessions) {
      const heading = dayHeading(session.startedAt, now);
      if (result.length === 0 || result[result.length - 1].heading !== heading) result.push({ heading, sessions: [] });
      result[result.length - 1].sessions.push(session);
    }
    return result;
  }, [data.sessions, now]);
  return (
    <div className="xo-sf-session-side">
      <div className="xo-mob-session-list xo-sf-session-list" role="listbox" aria-label={st('sessions.list_label')}>
        {groups.map((group) => (
          <div key={group.heading} className="xo-mob-session-group">
            <h4>{group.heading}</h4>
            {group.sessions.map((session) => <SessionRow key={session.id} session={session} active={session.id === selected} onPick={onPick} />)}
          </div>
        ))}
      </div>
      {data.total > PAGE_SIZE && (
        <div className="xo-mob-pager">
          <span>{st('showing_range', { from: NUMBER.format(offset + 1), to: NUMBER.format(offset + data.sessions.length), total: NUMBER.format(data.total) })}</span>
          <button type="button" className="xo-mob-icon-button" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))} aria-label={st('previous_page')}><ChevronLeft /></button>
          <button type="button" className="xo-mob-icon-button" disabled={offset + PAGE_SIZE >= data.total} onClick={() => onPage(offset + PAGE_SIZE)} aria-label={st('next_page')}><ChevronRight /></button>
        </div>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, children }) {
  return (
    <div className="xo-sf-fact">
      <dt><Icon aria-hidden="true" />{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function SessionFacts({ session, orders, pages }) {
  const names = useStoreNames();
  const utm = Object.entries(session.utm || {}).filter(([, value]) => value);
  const landing = pages.find((page) => page.path === session.landingPath) || null;
  const exit = session.exitPath || session.landingPath || '/';
  return (
    <dl className="xo-sf-facts">
      <Fact icon={Clock3} label={st('sessions.fact_when')}>
        <strong>{dateTime(session.startedAt)}</strong>
        <small>{st('sessions.fact_length', { total: lengthOf(session.durationMs), active: lengthOf(session.activeMs) })}</small>
      </Fact>
      <Fact icon={Megaphone} label={st('sessions.fact_source')}>
        <strong><ChannelMark channel={session.channel} /></strong>
        <small>{[session.source, session.medium].filter(Boolean).join(' / ') || st('sessions.direct')}</small>
        {session.campaign && (
          <Link className="xo-mob-link" to={`/xomics/store/campaigns?campaign=${session.campaign.id}`}>
            {session.campaign.name}{session.link ? ` · ${session.link.code}` : ''}<ChevronRight aria-hidden="true" />
          </Link>
        )}
        {session.referrerHost && <small>{st('sessions.referrer', { host: session.referrerHost, path: session.referrerPath || '' })}</small>}
        {utm.length > 0 && <span className="xo-sf-utm-chips">{utm.map(([name, value]) => <em key={name}><i>{st(`links.utm_${name}`)}</i>{value}</em>)}</span>}
        {session.clickIds && session.clickIds.length > 0 && (
          <span className="xo-sf-utm-chips">{session.clickIds.map((id) => <em key={id}><i>{st('sessions.ad_click')}</i>{clickIdName(id)}</em>)}</span>
        )}
      </Fact>
      <Fact icon={MonitorSmartphone} label={st('sessions.fact_device')}>
        <strong><DeviceMark device={session.deviceType} withLabel /></strong>
        <small>{[session.browser, session.os].filter(Boolean).join(' · ') || st('none')}</small>
        {session.screen && <small>{st('sessions.screen_plain', { w: session.screen.w, h: session.screen.h })}</small>}
      </Fact>
      <Fact icon={FlagIcon} label={st('sessions.fact_place')}>
        <strong>{session.country ? <Flag code={session.country} withName={!session.city} /> : null}{session.city ? ` ${session.city}` : ''}{!session.country && !session.city ? st('place_unknown') : ''}</strong>
        {session.region && <small>{session.region}</small>}
        <small>{[session.language ? languageName(session.language) : null, session.market ? st('sessions.market_line', { market: countryName(session.market) }) : null,
          session.currency ? currencyName(session.currency) : null].filter(Boolean).join(' · ')}</small>
      </Fact>
      <Fact icon={ArrowRight} label={st('sessions.fact_path')}>
        <strong><PathText path={session.landingPath || '/'} title={landing ? landing.title : null} /></strong>
        <small>{st('sessions.exit_on', { path: names.pageOrPath(exit) })}</small>
        <small>{stCount('pages_count', session.pageViews)}</small>
      </Fact>
      <Fact icon={Sparkles} label={st('sessions.fact_orders')}>
        {orders.length === 0 ? <strong className="is-muted">{session.checkout ? st('sessions.no_order_checkout') : st('sessions.no_order')}</strong> : orders.map((order) => (
          <span key={order.reference || order.placedAt} className="xo-sf-order-line">
            <strong>{money(order.totalMinor - order.refundedMinor, order.currency, { minorUnit: order.minorUnit })}</strong>
            <small><OrderReference reference={order.reference} /> · {codeText('order_status', order.status)}</small>
          </span>
        ))}
      </Fact>
    </dl>
  );
}

// Each page the visit saw, and inside it what happened, in order.
function Timeline({ detail, showAll }) {
  const names = useStoreNames();
  const start = detail.session.startedAt;
  const pages = useMemo(() => {
    const byId = new Map(detail.pages.map((page) => [page.id, { ...page, events: [] }]));
    const loose = [];
    for (const event of detail.events.map(asShown)) {
      if (!showAll && QUIET.has(event.type)) continue;
      if (event.type === 'page_view') continue;
      const page = event.page ? byId.get(event.page) : null;
      if (page) page.events.push(event); else loose.push(event);
    }
    return { list: [...byId.values()].sort((a, b) => a.startedAt - b.startedAt), loose };
  }, [detail, showAll]);
  const row = (event) => (
    <li key={event.id} className={`is-${eventTone(event.type)}`}>
      <span className="xo-sf-tl-time">+{lengthOf(Math.max(0, event.at - start))}</span>
      <span className="xo-sf-tl-icon"><EventIcon type={event.type} /></span>
      <span className="xo-sf-tl-main"><strong>{eventLabel(event.type)}</strong><small>{eventDetail(event, names)}</small></span>
    </li>
  );
  if (pages.list.length === 0 && pages.loose.length === 0) return <Empty title={st('sessions.no_events')} />;
  return (
    <ol className="xo-sf-timeline">
      {pages.list.map((page) => (
        <li key={page.id} className="xo-sf-tl-page">
          <div className="xo-sf-tl-page-head">
            <span className="xo-sf-tl-time">+{lengthOf(Math.max(0, page.startedAt - start))}</span>
            <span className="xo-sf-tl-icon is-page"><EventIcon type="page_view" /></span>
            <span className="xo-sf-tl-main">
              <strong title={page.path}>{page.title || names.pageOrPath(page.path)}</strong>
              <small>
                {[page.title ? page.path : null, page.loadMs !== null ? st('event.loaded_in', { time: duration(page.loadMs) }) : null,
                  page.activeMs !== null ? st('sessions.active_for', { time: lengthOf(page.activeMs) }) : null,
                  page.maxScrollPct !== null ? st('sessions.scrolled', { depth: page.maxScrollPct }) : null].filter(Boolean).join(' · ')}
              </small>
            </span>
          </div>
          {page.events.length > 0 && <ol className="xo-sf-tl-events">{page.events.map(row)}</ol>}
        </li>
      ))}
      {pages.loose.length > 0 && <li className="xo-sf-tl-page"><ol className="xo-sf-tl-events">{pages.loose.map(row)}</ol></li>}
    </ol>
  );
}

// The recorded page views of a session, in the order they were seen, each with its marks.
function recordingsOf(detail, names) {
  const firstAt = new Map();
  for (const chunk of detail.replay.chunks) {
    if (!firstAt.has(chunk.page) || chunk.firstAt < firstAt.get(chunk.page)) firstAt.set(chunk.page, chunk.firstAt);
  }
  const pages = new Map(detail.pages.map((page) => [page.id, page]));
  return [...firstAt.entries()].sort((a, b) => a[1] - b[1]).map(([page, at]) => ({
    page,
    path: pages.has(page) ? pages.get(page).path : null,
    title: pages.has(page) ? pages.get(page).title || names.page(pages.get(page).path) : null,
    startedAt: pages.has(page) ? pages.get(page).startedAt : at,
    markers: detail.events.map(asShown).filter((event) => event.page === page && MARKED[event.type])
      .map((event) => ({ at: event.at, kind: MARKED[event.type], label: `${eventLabel(event.type)} ${eventDetail(event, names)}`.trim() })),
  }));
}

function SessionDetail({ id, onClose }) {
  const load = useCallback(() => loadStorefrontSession(id), [id]);
  const report = useLiveResource(['store_live', 'store'], load, { reconcileMs: 30000 });
  const [showAll, setShowAll] = useState(false);
  const names = useStoreNames();
  const recordings = useMemo(() => (report.data ? recordingsOf(report.data, names) : []), [report.data, names]);
  if (report.loading && report.data === null) return <Loading label={st('sessions.reading_one')} />;
  if (report.error && report.data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const detail = report.data;
  const { session } = detail;
  return (
    <div className="xo-sf-session">
      <header className="xo-mob-stage-head xo-sf-session-head">
        <span className="xo-mob-avatar">{session.country ? <Flag code={session.country} /> : <Laptop aria-hidden="true" />}</span>
        <span className="xo-mob-stage-who">
          <strong>{placeOf(session)}{session.live && <span className="xo-sf-live-tag"><i className="xo-mob-live-dot" />{st('sessions.live_now')}</span>}</strong>
          <small>
            <span><DeviceMark device={session.deviceType} withLabel /></span>
            <span><ChannelMark channel={session.channel} /></span>
            {session.isBot && <span className="xo-mob-tag">{st('sessions.bot')}</span>}
          </small>
        </span>
        <span className="xo-mob-stage-when">
          <strong>{dateTime(session.startedAt)}</strong>
          <small>{st('sessions.session_length', { length: lengthOf(session.durationMs) })}</small>
        </span>
        {onClose && <button type="button" className="xo-mob-icon-button xo-sf-session-close" onClick={onClose} aria-label={st('close')}><X /></button>}
      </header>
      <SessionFacts session={session} orders={detail.orders} pages={detail.pages} />
      {recordings.length > 0 ? (
        <section className="xo-sf-session-block">
          <h3 className="xo-mob-subhead">{st('sessions.replay_title')}</h3>
          <StorefrontReplay sessionId={session.id} recordings={recordings} />
        </section>
      ) : (
        <p className="xo-sf-no-replay"><EyeOff aria-hidden="true" />{st('sessions.no_replay_plain')}</p>
      )}
      <section className="xo-sf-session-block">
        <div className="xo-sf-block-head">
          <h3 className="xo-mob-subhead">{st('sessions.timeline_title')}</h3>
          <button type="button" className="xo-sf-disclosure" aria-pressed={showAll} onClick={() => setShowAll(!showAll)}>
            {showAll ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}{showAll ? st('sessions.hide_quiet') : st('sessions.show_quiet')}
          </button>
        </div>
        <Timeline detail={detail} showAll={showAll} />
      </section>
    </div>
  );
}

// A row of choice chips: "any" first, then each value; one may be on.
function Choices({ value, options, onPick, label }) {
  return (
    <div className="xo-sf-choices" role="group" aria-label={label}>
      <button type="button" aria-pressed={!value} className={!value ? 'is-on' : ''} onClick={() => onPick('')}>{st('sessions.any')}</button>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={value === option.value} className={value === option.value ? 'is-on' : ''}
          onClick={() => onPick(value === option.value ? '' : option.value)} title={typeof option.label === 'string' ? option.label : undefined}>
          {option.icon}{option.label}
        </button>
      ))}
    </div>
  );
}

// The bar over the list: the channel and "paid" inline, as staff use them most;
// every other filter in one Filters menu; and a chip for each filter in use,
// linked ones (a page, a landing page, a link) included, to clear it.
function Filters({ params, options, onChange, linked = [] }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (box.current && !box.current.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('keydown', escape); };
  }, [open]);
  const pick = (key) => (value) => onChange({ [key]: value });
  const campaigns = options ? options.campaigns : [];
  const choices = {
    campaign: campaigns.map((campaign) => ({ value: String(campaign.id), label: campaign.name })),
    device: (options ? options.deviceTypes : []).map((device) => ({ value: device, label: deviceLabel(device), icon: <DeviceMark device={device} /> })),
    country: (options ? options.countries : []).map((code) => ({ value: code, label: countryName(code), icon: <Flag code={code} /> })),
    sort: [{ value: 'longest', label: st('sessions.sort_longest') }, { value: 'pages', label: st('sessions.sort_pages') }],
  };
  const tucked = ['campaign', 'device', 'country', 'sort'].filter((key) => params.get(key));
  const flags = FLAGS.filter((flag) => flag !== 'converted' && params.get(flag) === 'true');
  const inUse = tucked.length + flags.length;
  const nameOf = (key) => {
    const found = choices[key].find((option) => option.value === params.get(key));
    return found ? found.label : params.get(key);
  };
  const paid = params.get('converted') === 'true';
  return (
    <div className="xo-sf-filterbar">
      <FilterMenu icon={Megaphone} label={st('sessions.any_channel')} value={params.get('channel') || ''} onChange={pick('channel')}
        options={[{ value: '', label: st('sessions.any_channel') }, ...(options ? options.channels : []).map((channel) => ({ value: channel, label: channelLabel(channel) }))]} />
      <button type="button" className={`xo-sf-paid${paid ? ' is-on' : ''}`} aria-pressed={paid} onClick={() => onChange({ converted: paid ? '' : 'true' })}>
        <Sparkles aria-hidden="true" />{st('sessions.flag_converted')}
      </button>
      <div className="xo-sf-drawer" ref={box}>
        <button type="button" className={`xo-product-filter-button${inUse > 0 ? ' is-filtered' : ''}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}>
          <SlidersHorizontal aria-hidden="true" /><span>{st('sessions.filters')}</span>{inUse > 0 ? <b>{NUMBER.format(inUse)}</b> : <i />}<ChevronDown aria-hidden="true" />
        </button>
        {open && (
          <div className="xo-sf-drawer-panel" role="dialog" aria-label={st('sessions.filters_title')}>
            <header><strong>{st('sessions.filters_title')}</strong><small>{st('sessions.filters_detail')}</small></header>
            {choices.campaign.length > 0 && (
              <section>
                <h4>{st('sessions.group_where')}</h4>
                <Choices value={params.get('campaign') || ''} options={choices.campaign} onPick={pick('campaign')} label={st('sessions.any_campaign')} />
              </section>
            )}
            <section>
              <h4>{st('sessions.group_who')}</h4>
              <div className="xo-sf-choice-row"><Choices value={params.get('device') || ''} options={choices.device} onPick={pick('device')} label={st('sessions.any_device')} /></div>
              <div className="xo-sf-choice-row"><Choices value={params.get('country') || ''} options={choices.country} onPick={pick('country')} label={st('sessions.any_country')} /></div>
            </section>
            <section>
              <h4>{st('sessions.group_what')}</h4>
              <div className="xo-sf-choices" role="group" aria-label={st('sessions.only')}>
                {FLAGS.filter((flag) => flag !== 'converted').map((flag) => {
                  const on = params.get(flag) === 'true';
                  return (
                    <button key={flag} type="button" aria-pressed={on} className={on ? 'is-on' : ''} onClick={() => onChange({ [flag]: on ? '' : 'true' })}>
                      {st(`sessions.flag_${flag}`)}
                    </button>
                  );
                })}
              </div>
            </section>
            <section>
              <h4>{st('sessions.group_order')}</h4>
              <div className="xo-sf-choices" role="group" aria-label={st('sessions.group_order')}>
                {[{ value: '', label: st('sessions.sort_recent') }, ...choices.sort].map((option) => (
                  <button key={option.value || 'recent'} type="button" aria-pressed={(params.get('sort') || '') === option.value}
                    className={(params.get('sort') || '') === option.value ? 'is-on' : ''} onClick={() => onChange({ sort: option.value })}>{option.label}</button>
                ))}
              </div>
            </section>
            <footer>
              <button type="button" className="xo-btn-2" disabled={inUse === 0}
                onClick={() => onChange(Object.fromEntries([...tucked, ...flags].map((key) => [key, ''])))}>{st('sessions.clear_filters')}</button>
              <button type="button" className="xo-btn" onClick={() => setOpen(false)}>{st('sessions.done')}</button>
            </footer>
          </div>
        )}
      </div>
      <div className="xo-sf-active">
        {tucked.map((key) => (
          <button key={key} type="button" onClick={() => onChange({ [key]: '' })} title={st('sessions.clear_filters')}>
            <span>{nameOf(key)}</span><X aria-hidden="true" />
          </button>
        ))}
        {flags.map((flag) => (
          <button key={flag} type="button" onClick={() => onChange({ [flag]: '' })}><span>{st(`sessions.flag_${flag}`)}</span><X aria-hidden="true" /></button>
        ))}
        {linked.map((key) => (
          <button key={key} type="button" onClick={() => onChange({ [key]: '' })}>
            <CircleDot aria-hidden="true" /><span>{st(`sessions.linked_${key}`, { value: params.get(key) })}</span><X aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}

function SessionsView({ site, store }) {
  const [days, setDays] = useState(7);
  const [searchParams, setSearchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const detailRef = useRef(null);
  const now = Date.now();
  const filterKey = [...TEXT_FILTERS, ...FLAGS, 'sort'].map((key) => `${key}=${searchParams.get(key) || ''}`).join('&');
  useEffect(() => { setOffset(0); }, [filterKey, days]);

  const load = useCallback(() => {
    const filters = { site: site.code, days, limit: PAGE_SIZE, offset };
    for (const [key, value] of new URLSearchParams(filterKey).entries()) if (value) filters[key] = value;
    return loadStorefrontSessions(filters);
  }, [site.code, days, offset, filterKey]);
  const list = useLiveResource(['store_live', 'store'], load, { reconcileMs: 30000 });
  const loadOptions = useCallback(() => loadStorefrontFilters({ site: site.code, days }), [site.code, days]);
  const options = useLiveResource(['store'], loadOptions, { reconcileMs: 0 });

  const selected = searchParams.get('session') || (list.data && list.data.sessions[0] ? list.data.sessions[0].id : null);
  const pick = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set('session', id);
    setSearchParams(next, { replace: true });
    if (detailRef.current && window.matchMedia('(max-width: 1100px)').matches) detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [searchParams, setSearchParams]);
  const change = useCallback((values) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(values)) { if (value) next.set(key, value); else next.delete(key); }
    next.delete('session');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Filters that only arrive by link (a page, a landing page, a link) show as chips to clear.
  const linked = ['link', 'path', 'landing', 'source', 'browser'].filter((key) => searchParams.get(key));
  const data = list.data;
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      <StorefrontHead title={st('sessions.title')} subtitle={st('sessions.subtitle')} days={days} onDays={setDays} store={store} />
      <Filters params={searchParams} options={options.data} onChange={change} linked={linked} />
      {list.error && data !== null && <p className="xo-alert t-warn" role="alert">{list.error}</p>}
      {data === null ? <ReadState resource={list} label={st('sessions.reading')} /> : (
        <Panel title={st('sessions.panel_title')} className="is-player xo-sf-sessions-panel"
          detail={stCount('sessions.matching', data.total)}>
          {data.sessions.length === 0 ? <Empty title={st('sessions.none')} detail={st('sessions.none_detail')} /> : (
            <div className="xo-mob-player xo-sf-player">
              <SessionList data={data} selected={selected} onPick={pick} offset={offset} onPage={setOffset} now={now} />
              <div className="xo-mob-stage xo-sf-stage" ref={detailRef}>
                {selected && <SessionDetail key={selected} id={selected} />}
              </div>
            </div>
          )}
        </Panel>
      )}
      <p className="xo-mob-footnote">{st('sessions.footnote', { count: count(PAGE_SIZE) })}</p>
    </div>
  );
}

export default function StorefrontSessions() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <SessionsView site={site} store={store} />}</StorefrontGate>;
}
